/**
 * Pure bucket classifier for today's appointment worklist (Flutter parity:
 * appointment_list_widget.dart getList()).
 *
 * Rules:
 *  - Completed   = status.code is 'Fulfilled'
 *  - In-Progress = NOT Fulfilled AND the appointment has a DRAFT encounter
 *                  (its id is in `draftApptIds`, derived from
 *                  GET /api/Patients/GetEncounterListByDraftStatus — see
 *                  use-draft-encounters.ts)
 *  - Upcoming    = everything else (NOT Fulfilled AND no draft encounter)
 *
 * PROD-1300: the /api/Appointment LIST wire does NOT carry an encounterId on the
 * row, so the prior `item.encounterId` check (TODO PROD-1125) left In-Progress
 * permanently empty and those appointments fell to Upcoming. Flutter matches the
 * appointment against the separately-fetched draft-encounter list by
 * `encounter.appointment == appointment.id`; this mirrors that via `draftApptIds`.
 */
import type { ListAppointmentItem } from '@/types/appointment-api';

export interface AppointmentBuckets {
  upcoming: ListAppointmentItem[];
  inProgress: ListAppointmentItem[];
  completed: ListAppointmentItem[];
}

export function bucketAppointments(
  items: ListAppointmentItem[],
  draftApptIds: ReadonlySet<string> = new Set(),
): AppointmentBuckets {
  const upcoming: ListAppointmentItem[] = [];
  const inProgress: ListAppointmentItem[] = [];
  const completed: ListAppointmentItem[] = [];

  for (const item of items) {
    // Availability blocks (isBlock) are NOT patient appointments — they come back with null
    // status/appointmentType/patient and must never enter the worklist. Reading item.status.code
    // on a block was crashing the whole /today page ("Cannot read properties of null (reading
    // 'code')") whenever the day had a block row. Skip them (Flutter shows blocks only on the
    // calendar). The `?.` then defends any other null-status row.
    if (item.isBlock) continue;
    const statusCode = item.status?.code;

    if (statusCode === 'Fulfilled') {
      completed.push(item);
    } else if (draftApptIds.has(item.id)) {
      // A draft (in-progress) encounter exists for this appointment.
      inProgress.push(item);
    } else {
      upcoming.push(item);
    }
  }

  return { upcoming, inProgress, completed };
}
